import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {
  IAIReceptionistSettings,
  IPractice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  initFirestoreModel,
  WithRef,
} from '@principle-theorem/shared';
import { switchMap, tap } from 'rxjs/operators';
import { AIReceptionistSettingsService } from '../services/ai-receptionist-settings.service';

export interface IAiReceptionistSettingsEditorState {
  practice?: WithRef<IPractice>;
  settings?: IAIReceptionistSettings;
  dirtySections: Partial<Record<keyof IAIReceptionistSettings, boolean>>;
  saving: boolean;
  lastSaveError?: string;
}

const initialState: IAiReceptionistSettingsEditorState = {
  settings: undefined,
  dirtySections: {},
  saving: false,
  lastSaveError: undefined,
};

@Injectable()
export class AiReceptionistSettingsEditorStore extends ComponentStore<IAiReceptionistSettingsEditorState> {
  readonly settings$ = this.select((state) => state.settings);
  readonly dirtySections$ = this.select((state) => state.dirtySections);
  readonly saving$ = this.select((state) => state.saving);
  readonly lastSaveError$ = this.select((state) => state.lastSaveError);
  readonly practice$ = this.select((state) => state.practice);
  readonly isDirty$ = this.select((state) =>
    Object.values(state.dirtySections).some(Boolean)
  );

  readonly setSettings = this.updater(
    (state, settings: IAIReceptionistSettings | undefined) => ({
      ...state,
      settings,
      dirtySections: {},
    })
  );

  readonly setPractice = this.updater(
    (state, practice: WithRef<IPractice>) => ({
      ...state,
      practice,
    })
  );

  readonly setSaving = this.updater((state, saving: boolean) => ({
    ...state,
    saving,
  }));

  readonly setLastSaveError = this.updater(
    (state, error: string | undefined) => ({
      ...state,
      lastSaveError: error,
    })
  );

  readonly clearDirtySections = this.updater((state) => ({
    ...state,
    dirtySections: {},
  }));

  /**
   * Update a single section of the settings and mark it dirty.
   * No-op when settings have not yet been loaded (undefined).
   */
  readonly updateSection = this.updater(
    (
      state: IAiReceptionistSettingsEditorState,
      update: {
        section: keyof IAIReceptionistSettings;
        data: IAIReceptionistSettings[keyof IAIReceptionistSettings];
      }
    ) => {
      if (!state.settings) {
        return state;
      }
      return {
        ...state,
        settings: { ...state.settings, [update.section]: update.data },
        dirtySections: { ...state.dirtySections, [update.section]: true },
      };
    }
  );

  /**
   * Persist the current settings to Firestore via AIReceptionistSettingsService.
   * The onWrite cloud trigger is responsible for pushing changes to Ovie —
   * this effect does NOT call any Ovie callable directly.
   */
  readonly saveSettingsAndPush = this.effect<{
    practice: WithRef<IPractice>;
    stafferRef?: DocumentReference<IStaffer>;
  }>((trigger$) =>
    trigger$.pipe(
      tap(() => this.setSaving(true)),
      switchMap(async ({ practice, stafferRef }) => {
        try {
          const currentSettings = this.get().settings;
          const settingsToSave = currentSettings ?? this._emptySettings();

          await AIReceptionistSettingsService.saveSettings(
            practice,
            settingsToSave,
            stafferRef
          );

          this.setSaving(false);
          this.setLastSaveError(undefined);
          this.clearDirtySections();
          return { success: true };
        } catch (error) {
          const errorMsg =
            error instanceof Error ? error.message : 'Save failed';
          this.setSaving(false);
          this.setLastSaveError(errorMsg);
          return { success: false, error: errorMsg };
        }
      })
    )
  );

  constructor() {
    super(initialState);
  }

  getState(): IAiReceptionistSettingsEditorState {
    return this.get();
  }

  private _emptySettings(): IAIReceptionistSettings {
    return {
      ...initFirestoreModel(),
    };
  }
}
